import * as React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Tooltip,
  FormHelperText,
} from '@mui/material';
import { Globe, Key, Code, Copy, Check } from 'lucide-mui';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import html from 'react-syntax-highlighter/dist/esm/languages/hljs/htmlbars';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { githubGist as codeStyles } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { RootState } from '../../reducers';
import { updateSettings, SettingsKeys } from './settingsSlice';
import { setUpPluginData } from '../plugin/pluginSlice';
import { ToggleSetting, SettingsSection, CopyButton } from './common';
import { useTheme as useCustomTheme } from '../../theme/ThemeContext';
import config from '../../../config';

SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('htmlbars', html);
SyntaxHighlighter.registerLanguage('xml', xml);

const CodeBlock = ({ children: code }: { children: string }) => {
  const { isDarkMode } = useCustomTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 1,
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 1,
          }}
        >
          <CopyButton text={code} />
        </Box>
        <SyntaxHighlighter
          language="htmlbars"
          style={codeStyles}
          customStyle={{
            backgroundColor: isDarkMode ? '#1A1B1C' : '#F9FAFB',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: isDarkMode ? '#2A2B2C' : '#E5E7EB',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            margin: 0,
          }}
        >
          {code}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
};

const PluginSettings = () => {
  const dispatch = useDispatch();
  const { plugin, settings, plan } = useSelector((state: RootState) => state);
  const pluginSettings = settings.plugin;
  const { _id, website } = plugin;
  const userPlan = plan.subscription.package;

  const [modifiedSettings, setModifiedSettings] = useState(pluginSettings);
  const [newWebsite, setNewWebsite] = useState({
    value: website,
    isError: false,
  });
  const [tokenCopied, setTokenCopied] = useState(false);

  useEffect(() => {
    setModifiedSettings(pluginSettings);
  }, [pluginSettings]);

  useEffect(() => {
    setNewWebsite({ value: website, isError: false });
  }, [website]); // Add this useEffect to update newWebsite when website changes

  const validateWebsite = (input: string) => {
    return !input || !input.match(/^(?:\w+\.)*\w+(?::[0-9]{1,5})?(?:\/\w+)*$/i);
  };

  const handleChangeWebSiteField = (event: { target: { value: string } }) => {
    const isError = validateWebsite(event.target.value);
    setNewWebsite({ value: event.target.value, isError });
  };

  const handleCopyToken = async () => {
    await navigator.clipboard.writeText(_id);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const handleSave = () => {
    dispatch(
      updateSettings({
        key: 'plugin' as SettingsKeys,
        newSettings: modifiedSettings,
      })
    );
    if (!validateWebsite(newWebsite.value)) {
      dispatch(setUpPluginData(newWebsite.value));
    }
  };

  const getHelperText = () => {
    if (
      newWebsite.isError &&
      (userPlan === 'BUSINESS' || userPlan === 'ENTERPRISE')
    ) {
      return 'Please enter your website correctly.';
    }
    if (userPlan === 'FREE_LOGGEDIN' || userPlan === 'GROWTH') {
      return 'Plugin is available exclusively on the Business plan.';
    }
    return '';
  };

  const mainSnippet = `<script
  charset="UTF-8" type="text/javascript"
  src="${config.appBaseUrl}/plugin.js">
</script>

<script>
  function callback({ id, url }) {
    /* When the recording ends, you can get the recording id and the URL here */
  }

  function loadScreenApp() {
    const screenApp = new window.ScreenApp('${plugin._id}', callback);
    screenApp.mount("#screenapp-plugin");
  }
</script>

<div id="screenapp-plugin" />`;

  const exampleCallSnippet = `<button onclick="loadScreenApp()">Report Issue</button>`;

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      <SettingsSection title="Configuration">
        <Box sx={{ mb: 3 }}>
          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label="Website URL"
            description="The domain where your plugin will be installed"
            icon={<Globe />}
          >
            <TextField
              value={newWebsite.value}
              onChange={handleChangeWebSiteField}
              error={
                newWebsite.isError ||
                userPlan === 'FREE_LOGGEDIN' ||
                userPlan === 'GROWTH'
              }
              placeholder="Add URL here"
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      newWebsite.isError ||
                      userPlan === 'FREE_LOGGEDIN' ||
                      userPlan === 'GROWTH'
                        ? 'red'
                        : 'default',
                  },
                  '&:hover fieldset': {
                    borderColor:
                      newWebsite.isError ||
                      userPlan === 'FREE_LOGGEDIN' ||
                      userPlan === 'GROWTH'
                        ? 'red'
                        : 'default',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor:
                      newWebsite.isError ||
                      userPlan === 'FREE_LOGGEDIN' ||
                      userPlan === 'GROWTH'
                        ? 'red'
                        : 'default',
                  },
                },
              }}
            />
            <FormHelperText
              error={
                newWebsite.isError ||
                userPlan === 'FREE_LOGGEDIN' ||
                userPlan === 'GROWTH'
              }
            >
              {getHelperText()}
            </FormHelperText>
          </ToggleSetting>

          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label="Plugin Token"
            description="Your unique plugin identifier"
            icon={<Key />}
          >
            <Box sx={{ position: 'relative', mt: { xs: 2, sm: 0 } }}>
              <TextField
                value={_id || ''}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: {
                    pr: 5,
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                }}
              >
                <Tooltip
                  title={tokenCopied ? 'Copied!' : 'Copy'}
                  placement="bottom"
                  arrow
                >
                  <IconButton
                    type="button"
                    aria-label="Copy"
                    onClick={handleCopyToken}
                    disabled={tokenCopied}
                    sx={{
                      padding: 0.5,
                      color: 'secondary.main',
                      backgroundColor: 'transparent',
                      height: '30px',
                      width: '30px',
                      '&:hover': {
                        backgroundColor: 'secondary.main',
                        color: 'common.white',
                      },
                    }}
                  >
                    {tokenCopied ? (
                      <Check sx={{ width: 16, height: 16 }} />
                    ) : (
                      <Copy sx={{ width: 16, height: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </ToggleSetting>
        </Box>
      </SettingsSection>

      <SettingsSection title="Integration Code">
        <Box sx={{ mb: 3 }}>
          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label="Installation Code"
            description="Add this code to your website to enable the plugin"
            icon={<Code />}
          >
            <Box sx={{ mt: { xs: 2, sm: 0 } }}>
              <CodeBlock>{mainSnippet}</CodeBlock>
            </Box>
          </ToggleSetting>

          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label="Example Usage"
            description="Example of how to trigger the plugin"
            icon={<Code />}
          >
            <Box sx={{ mt: { xs: 2, sm: 0 } }}>
              <CodeBlock>{exampleCallSnippet}</CodeBlock>
            </Box>
          </ToggleSetting>
        </Box>
      </SettingsSection>

      <Box sx={{ mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ minWidth: 120 }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default PluginSettings;
